import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useCallback,
} from 'react';
import { AuditLog, addAuditLog, getAuditLogs } from '../services/auditLog';

interface AuditLogContextType {
    logs: AuditLog[];
    addLog: (
        action: string,
        model_id: string,
        model_type: string,
        user_id: string,
        properties: Record<string, any>,
        host: string
    ) => Promise<void>;
    fetchLogs: (lastEvaluatedKey?: any) => Promise<void>;
    hasMore: boolean;
}

const AuditLogContext = createContext<AuditLogContextType | undefined>(
    undefined
);

export const AuditLogProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>(null);
    const [hasMore, setHasMore] = useState<boolean>(true);

    const addLog = useCallback(
        async (
            action: string,
            model_id: string,
            model_type: string,
            user_id: string,
            properties: Record<string, any>,
            host: string
        ) => {
            try {
                await addAuditLog({
                    action,
                    model_id,
                    model_type,
                    user_id,
                    properties,
                    host,
                });
            } catch (error) {
                console.error('Error adding audit log:', error);
            }
        },
        []
    );

    const fetchLogs = useCallback(async (loadMore = false) => {
        try {
            const key = loadMore ? lastEvaluatedKey : undefined;
            const { logs: fetchedLogs, lastEvaluatedKey: newKey } = await getAuditLogs(key);
            setLogs(prevLogs => loadMore ? [...prevLogs, ...fetchedLogs] : fetchedLogs);
            setLastEvaluatedKey(newKey);
            setHasMore(!!newKey);
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        }
    }, [lastEvaluatedKey]);

    return (
        <AuditLogContext.Provider value={{ logs, addLog, fetchLogs, hasMore }}>
            {children}
        </AuditLogContext.Provider>
    );
};

export const useAuditLog = () => {
    const context = useContext(AuditLogContext);
    if (!context) {
        throw new Error('useAuditLog must be used within an AuditLogProvider');
    }
    return context;
};
